import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";
import { useAuth } from "../../components/context/LoginContext/AuthContext";

/**
 * @typedef {Object} Tier
 * @property {number} quantity
 * @property {number} price
 */

/**
 * @typedef {Object} Plan
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {string} remark
 * @property {string|null} image
 * @property {string} currency
 * @property {string} final_price
 * @property {string} base_price
 * @property {number} discount_percent
 * @property {string} billing_cycle
 * @property {boolean} is_trial_enabled
 * @property {number} trial_days
 * @property {Tier[]} tiers
 */

/**
 * Custom hook to fetch available subscription plans.
 */
export const useGetPlan = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /** @type {[Plan[]|null, Function]} */
  const [data, setData] = useState(null);

  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Fetches the available plans from the API.
   * @param {Object} [options]
   * @param {string} [options.token] - Optional parent token. When provided, uses raw fetch
   *   with this token instead of fetchWithAuth. Used by AddStudentPage where the session
   *   is a student but we need to call this API as the parent.
   */
  const fetchPlans = async ({ token } = {}) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      let response;
      if (token) {
        response = await fetch(`${apiUrl}/plans`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        response = await fetchWithAuth(`${apiUrl}/plans`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
      }

      if (!response.ok) {
        let errorMsg = ERROR_MESSAGES?.UNKNOWN || "Something went wrong";
        const errorData = await response.json();
        if (errorData?.errors) {
          const errorKey = Object.keys(errorData.errors)[0];
          errorMsg = errorData.errors[errorKey][0];
        } else {
          errorMsg = errorData?.message || errorMsg;
        }
        if (response.status === 500) {
          errorMsg = ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
        }
        throw new Error(errorMsg);
      }

      const responseData = await response.json();
      setData(responseData?.plans || []);
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchPlans,
    loading,
    error,
    data,
    setError,
  };
};
